import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

const PADDING = 8;
const POLL_MS = 200;
const TIMEOUT_MS = 8000;
const REFRESH_MS = 250;
const VISIBLE_MS = 8000;

// Ring-only, non-blocking highlight of a [data-tour] anchor, requested by the
// copilot. Same geometry as the onboarding tour spotlight, but no dim: the
// user keeps full control of the page. Auto-dismisses after a few seconds or
// on any click.
const CopilotHighlight = ({ anchor, onDone }) => {
  const [rect, setRect] = useState(null);

  useEffect(() => {
    if (!anchor) return undefined;
    let cancelled = false;
    let element = null;
    let dismissTimer = null;
    const startedAt = Date.now();

    const measure = () => {
      if (cancelled || !element || !document.contains(element)) return;
      const r = element.getBoundingClientRect();
      setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    };

    const dismiss = () => {
      if (cancelled) return;
      cancelled = true;
      onDone && onDone();
    };

    const acquire = () => {
      if (cancelled) return;
      element = document.querySelector(`[data-tour="${anchor}"]`);
      if (element) {
        try {
          element.scrollIntoView({ block: 'center', behavior: 'smooth' });
        } catch (e) {
          /* older browsers */
        }
        setTimeout(measure, 350);
        dismissTimer = setTimeout(dismiss, VISIBLE_MS);
      } else if (Date.now() - startedAt > TIMEOUT_MS) {
        dismiss();
      } else {
        setTimeout(acquire, POLL_MS);
      }
    };
    acquire();

    const interval = setInterval(measure, REFRESH_MS);
    const onClick = () => dismiss();
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    window.addEventListener('click', onClick, true);
    return () => {
      cancelled = true;
      clearInterval(interval);
      if (dismissTimer) clearTimeout(dismissTimer);
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
      window.removeEventListener('click', onClick, true);
    };
  }, [anchor, onDone]);

  if (!anchor || !rect) return null;

  return createPortal(
    <div
      className="copilot-highlight-ring"
      style={{
        top: rect.top - PADDING,
        left: rect.left - PADDING,
        width: rect.width + PADDING * 2,
        height: rect.height + PADDING * 2,
      }}
    />,
    document.body
  );
};

export default CopilotHighlight;
